(() => {
  if (customElements.get('oilogic-search')) return;

class OilogicSearch extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.drawer = this.querySelector('[id="SearchDrawer"]');
    this.backdrop = this.querySelector('[data-search-backdrop]');
    this.input = this.querySelector('[data-search-input]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.output = this.querySelector('[data-search-output]');
    this.defaultPanel = this.querySelector('[data-search-default]');
    this.predictiveUrl = this.dataset.predictiveUrl;
    this.predictiveSection = this.dataset.predictiveSection;

    this.open = false;
    this.debounceTimer = null;
    this.fetchController = null;
    this.toggles = Array.from(document.querySelectorAll('[data-search-toggle]'));
    this.lastToggle = null;

    this.toggles.forEach((toggle) => {
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        this.lastToggle = toggle;
        this.openDrawer();
      }, { signal });
    });

    this.querySelectorAll('[data-search-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer(), { signal });
    });
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.closeDrawer(), { signal });
    }

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.open) this.closeDrawer();
      if (event.key === 'Tab' && this.open) this.trapFocus(event);
    }, { signal });

    if (this.input) {
      this.input.addEventListener('input', () => this.onInput(), { signal });
    }
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.reset(true), { signal });
    }

    // Suggested-search pills are real links (full navigation to /search) —
    // flag the clicked one busy so its spinner shows while the next page loads.
    this.addEventListener('click', (event) => {
      const pill = event.target.closest('.search__suggest-pill');
      if (pill) pill.setAttribute('data-busy', '');
    }, { signal });
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    if (this.fetchController) this.fetchController.abort();
    clearTimeout(this.debounceTimer);
    document.body.style.overflow = '';
  }

  openDrawer() {
    if (this.open) return;
    this.open = true;

    if (this.backdrop) this.backdrop.hidden = false;
    this.drawer.hidden = false;
    requestAnimationFrame(() => {
      if (this.backdrop) this.backdrop.classList.add('search__backdrop--open');
      this.drawer.classList.add('search__drawer--open');
    });

    document.body.style.overflow = 'hidden';
    this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
    if (this.input) this.input.focus();
  }

  closeDrawer() {
    if (!this.open) return;
    this.open = false;

    this.drawer.classList.remove('search__drawer--open');
    if (this.backdrop) this.backdrop.classList.remove('search__backdrop--open');

    const finish = () => {
      clearTimeout(fallback);
      this.drawer.hidden = true;
      if (this.backdrop) this.backdrop.hidden = true;
      this.drawer.removeEventListener('transitionend', onEnd);
    };
    const onEnd = (event) => {
      if (event.target === this.drawer && event.propertyName === 'transform') finish();
    };
    this.drawer.addEventListener('transitionend', onEnd);
    const fallback = setTimeout(finish, 400);

    document.body.style.overflow = '';
    this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    if (this.lastToggle) this.lastToggle.focus();
  }

  onInput() {
    clearTimeout(this.debounceTimer);
    const term = this.input.value.trim();
    this.clearButton.hidden = term.length === 0;

    if (term.length === 0) {
      this.reset(false);
      return;
    }

    this.debounceTimer = setTimeout(() => this.search(term), 250);
  }

  async search(term) {
    if (this.fetchController) this.fetchController.abort();
    this.fetchController = new AbortController();

    if (this.defaultPanel) this.defaultPanel.hidden = true;

    const params = new URLSearchParams({
      q: term,
      section_id: this.predictiveSection,
      'resources[type]': 'product',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
    });

    try {
      const response = await fetch(`${this.predictiveUrl}?${params}`, { signal: this.fetchController.signal });
      if (!response.ok) return;
      this.output.innerHTML = await response.text();
      this.fillEmptySuggestions();
    } catch (error) {
      if (error.name !== 'AbortError') this.output.innerHTML = '';
    }
  }

  // On no results, "Try these instead" reuses the same suggested-search pills
  // already rendered in .search__default (server-rendered from section
  // blocks) instead of duplicating that block configuration here.
  fillEmptySuggestions() {
    const placeholder = this.output.querySelector('[data-search-empty-suggestions]');
    if (!placeholder) return;

    const suggestList = this.defaultPanel?.querySelector('.search__suggest');
    const pills = suggestList?.querySelectorAll('.search__suggest-pill');
    if (!suggestList || !pills?.length) return;

    const heading = document.createElement('p');
    heading.className = 'search__suggest-heading';
    heading.textContent = placeholder.dataset.tryTheseLabel;

    const clone = suggestList.cloneNode(true);
    // Drop the block wrapper ids so the clone doesn't duplicate the ids
    // already present on .search__default's copy.
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));

    placeholder.append(heading, clone);
  }

  reset(refocus) {
    clearTimeout(this.debounceTimer);
    if (this.fetchController) this.fetchController.abort();
    if (this.input) this.input.value = '';
    if (this.clearButton) this.clearButton.hidden = true;
    if (this.output) this.output.innerHTML = '';
    if (this.defaultPanel) this.defaultPanel.hidden = false;
    if (refocus && this.input) this.input.focus();
  }

  focusables() {
    return Array.from(
      this.drawer.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);
  }

  trapFocus(event) {
    const focusable = this.focusables();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!this.drawer.contains(document.activeElement)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define('oilogic-search', OilogicSearch);
})();
